// Pure embed-URL helpers — no server-only / no Supabase. Lives here so the
// partner-side <EmbedDrawer> (Phase 7 sub-bucket 8) can normalise an
// `embed_url` before mounting the iframe. The worker has its own copy at
// apps/api/src/services/content-embed.ts; both should stay in lock-step (the
// admin-side embed renderer in `services/content.ts::resolveEmbedRenderer`
// drives the same normalisation server-side).
//
// Verbatim port of /lib/content-embed.ts.

import type { EmbedProvider } from "@colab/shared";

/**
 * Provider-specific iframe URL normalisation. Inputs may be the "share" URL
 * a marketer would paste; outputs are always the canonical embed URL that
 * the provider hosts behind X-Frame-Options. Unrecognised input shapes are
 * returned as-is — providers like `webpage` use the raw URL with a sandboxed
 * iframe at the call site.
 */
export function embedUrlForProvider(provider: EmbedProvider, raw: string): string {
  const url = raw.trim();
  switch (provider) {
    case "loom": {
      const m = url.match(/loom\.com\/(?:share|embed)\/([A-Za-z0-9]+)/);
      if (m) return `https://www.loom.com/embed/${m[1]}`;
      return url;
    }
    case "youtube": {
      const m =
        url.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/) ??
        url.match(/youtube\.com\/watch\?v=([A-Za-z0-9_-]{6,})/) ??
        url.match(/youtube\.com\/embed\/([A-Za-z0-9_-]{6,})/);
      if (m) return `https://www.youtube.com/embed/${m[1]}`;
      return url;
    }
    case "vimeo": {
      const m =
        url.match(/vimeo\.com\/(?:video\/)?(\d+)/) ??
        url.match(/player\.vimeo\.com\/video\/(\d+)/);
      if (m) return `https://player.vimeo.com/video/${m[1]}`;
      return url;
    }
    case "google_slides":
    case "google_docs":
    case "google_sheets":
      return url.replace(/\/edit(\?[^#]*)?(#.*)?$/, "/preview");
    case "hubspot_meeting": {
      if (url.includes("embed=true")) return url;
      return url + (url.includes("?") ? "&" : "?") + "embed=true";
    }
    case "linkedin":
      return url;
    case "webpage":
      return url;
  }
}
